import { parse } from 'date-fns';

import type { RelationConfig } from '../models/base';

/**
 * Relation helpers for use in static relations() definitions.
 *
 * Usage:
 *   static override relations(): RelationConfig {
 *     return {
 *       date: dateYMDRelation,
 *       startTime: timeMinuteRelation,
 *       createdAt: dateRelation,
 *     };
 *   }
 */
export const dateYMDRelation: RelationConfig[string] = {
  transform: (v: number | null) => v != null ? DateYMD.fromInteger(v) : null,
  serialize: (v: DateYMD | null) => v != null ? v.integer : null,
};

export const timeMinuteRelation: RelationConfig[string] = {
  transform: (v: number | null) => v != null ? TimeMinute.fromInteger(v) : null,
  serialize: (v: TimeMinute | null) => v != null ? v.integer : null,
};

export const dateRelation: RelationConfig[string] = {
  transform: (v: number | null) => v != null ? new Date(v * 1000) : null,
  serialize: (v: Date | null) => v != null ? Math.floor(v.getTime() / 1000) : null,
};

/**
 * DateYMD - Represents a date as an integer in YYYYMMDD format.
 * Used throughout the API for date fields (e.g., 20260224 = Feb 24, 2026).
 */
export class DateYMD {
  value: number;

  constructor(value: number) {
    this.value = value;
  }

  static fromInteger(value: number | { value: number }): DateYMD {
    if (value !== null && typeof value === 'object') {
      value = value.value;
    }
    return new DateYMD(value);
  }

  static fromDate(date: Date): DateYMD {
    const zeroPad = (num: number): string => {
      return num < 10 ? '0' + num.toString() : num.toString();
    };

    const integer = parseInt(
      date.getUTCFullYear().toString() +
      zeroPad(date.getUTCMonth() + 1) +
      zeroPad(date.getUTCDate())
    );

    return new DateYMD(integer);
  }

  get date(): Date {
    return parse(this.value.toString(), 'yyyyMMdd', new Date());
  }

  get integer(): number {
    return this.value;
  }

  valueOf(): number {
    return this.value;
  }

  [Symbol.toPrimitive](_hint: string): number {
    return this.value;
  }

  toJSON(): number {
    return this.value;
  }
}

/**
 * TimeMinute - Represents a time as minutes from midnight.
 * Used throughout the API for time fields (e.g., 540 = 9:00 AM).
 */
export class TimeMinute {
  value: number;

  constructor(value: number) {
    this.value = value;
  }

  static fromInteger(value: number): TimeMinute {
    return new TimeMinute(value);
  }

  static fromDate(date: Date): TimeMinute {
    const integer = (date.getHours() * 60) + date.getMinutes();
    return new TimeMinute(integer);
  }

  get date(): Date {
    const hours = Math.floor(this.value / 60);
    const minutes = this.value - (hours * 60);
    return new Date(2000, 0, 1, hours, minutes);
  }

  get integer(): number {
    return this.value;
  }

  valueOf(): number {
    return this.value;
  }

  [Symbol.toPrimitive](_hint: string): number {
    return this.value;
  }

  toJSON(): number {
    return this.value;
  }
}
